<template>
  <div
    v-if="!isFolder"
    :id="`file-wrapper-${item.id}`"
    class="file-wrapper"
    spellcheck="false"
    @mouseup.stop="clickedItem"
  >
    <!--Grid preview-->
    <div
      class="file-item"
      :class="{
        'is-clicked': isClicked,
        'no-clicked': !isClicked && $isMobile(),
      }"
    >
      <div class="file-item-context">
        <div
          v-if="!fileStatusDownloading && !isImage"
          class="file-download"
          @click="goToItem"
        >
          <font-awesome-icon
            class="file-download-icon"
            icon="file-download"
          ></font-awesome-icon>
        </div>
        <div v-if="isImage" class="file-download" @click="goToItem">
          <font-awesome-icon
            class="file-download-icon"
            icon="eye"
          ></font-awesome-icon>
        </div>
      </div>
      <div :id="`file-item-status-${item.id}`" class="file-item-status">
        <div class="file-download-status">
          <template v-if="downloadProgress !== null">
            Atsiuntimas: {{ downloadProgress !== null ? downloadProgress : 0 }}%
          </template>
          <template v-else>
            Apdorojama, prašome palaukti
            <font-awesome-icon
              class="spinner-icon fa-spin"
              icon="spinner"
            ></font-awesome-icon>
          </template>
        </div>
      </div>
      <!--Thumbnail for item-->
      <div class="icon-item">
        <!-- MultiSelecting for the mobile version -->
        <div
          v-if="multiSelectMode"
          :class="{
            'check-select-folder': item.type === 'folder',
            'check-select': item.type !== 'folder',
          }"
        >
          <div class="select-box" :class="{ 'select-box-active': isClicked }">
            <CheckIcon v-if="isClicked" class="icon" size="17"></CheckIcon>
          </div>
        </div>

        <!--If is file or image, then link item-->
        <!--        <span
          v-if="isFile || (isImage && !item.thumbnail)"
          class="file-icon-text"
        >
          {{ item.mimetype }}
        </span>-->

        <!-- File types -->
        <font-awesome-icon
          v-if="isPdf"
          class="file-icon file-icon-pdf"
          icon="file-pdf"
        ></font-awesome-icon>
        <font-awesome-icon
          v-else-if="isDocument"
          class="file-icon file-icon-document"
          icon="file-word"
        ></font-awesome-icon>
        <font-awesome-icon
          v-else-if="isSpreadsheet"
          class="file-icon file-icon-spreadsheet"
          icon="file-excel"
        ></font-awesome-icon>
        <font-awesome-icon
          v-else-if="isPresentation"
          class="file-icon file-icon-presentation"
          icon="file-powerpoint"
        ></font-awesome-icon>
        <font-awesome-icon
          v-else-if="isZip"
          class="file-icon file-icon-zip"
          icon="file-archive"
        ></font-awesome-icon>
        <font-awesome-icon
          v-else-if="isCsv"
          class="file-icon file-icon-csv"
          icon="file-csv"
        ></font-awesome-icon>
        <font-awesome-icon
          v-else-if="isPlain"
          class="file-icon file-icon-plain"
          icon="file-alt"
        ></font-awesome-icon>
        <font-awesome-icon
          v-else-if="isFile || (isImage && !item.thumbnail)"
          class="file-icon file-icon-default"
          icon="file"
        ></font-awesome-icon>
        <template v-else></template>

        <!--Image thumbnail-->
        <img
          v-if="isImage && item.thumbnail"
          loading="lazy"
          class="image"
          :src="item.thumbnail"
          :alt="item.name"
        />
      </div>

      <!--Name-->
      <div class="item-name">
        <!--Name-->
        <b
          v-if="showFileName != 0"
          :ref="item.unique_id"
          class="name"
          @click.stop
        >
          {{ itemName }}
        </b>

        <div class="item-info">
          <!--Filesize-->
          <span v-if="!isFolder && showFileSize != 0" class="item-size">{{
            item.filesize
          }}</span>
        </div>
      </div>

      <span
        v-if="$isMobile() && !multiSelectMode && canShowMobileOptions"
        class="show-actions"
        @mousedown.stop="showItemActions"
      >
        <font-awesome-icon
          icon="ellipsis-h"
          class="icon-action"
        ></font-awesome-icon>
      </span>
    </div>
  </div>
</template>

<script>
import { LinkIcon, UserPlusIcon, CheckIcon } from 'vue-feather-icons'
import { debounce } from 'lodash'
import { mapGetters } from 'vuex'
import { events } from '@main/js/bus'

export default {
  name: 'FileItemGrid',
  components: {
    CheckIcon,
  },
  props: {
    item: {
      type: Object,
      default: () => {},
    },
    showFileName: {
      type: [Boolean, Number, String],
      default: 0,
    },
    showFileSize: {
      type: [Boolean, Number, String],
      default: 0,
    },
    showFileExtension: {
      type: [Boolean, Number, String],
      default: 0,
    },
  },
  data() {
    return {
      area: false,
      itemName: undefined,
      multiSelectMode: false,
      fileStatusDownloading: false,
      downloadProgress: null,
    }
  },
  computed: {
    ...mapGetters([
      'FilePreviewType',
      'sharedDetail',
      'fileInfoDetail',
      'data',
    ]),
    isClicked() {
      return false
    },
    isFolder() {
      return this.item.type === 'folder'
    },
    isFile() {
      return this.item.type !== 'folder' && this.item.type !== 'image'
    },
    isPdf() {
      return this.item.mimetype === 'pdf'
    },
    isDocument() {
      let mimetypes = [
        'odt',
        'vnd.oasis.opendocument.text',
        'doc',
        'ms-doc',
        'msword',
        'vnd.openxmlformats-officedocument.wordprocessingml.document',
      ]
      return mimetypes.includes(this.item.mimetype)
    },
    isSpreadsheet() {
      let mimetypes = [
        'ods',
        'vnd.oasis.opendocument.spreadsheet',
        'excel',
        'vnd.ms-excel',
        'x-excel',
        'x-msexcel',
        'vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      ]
      return mimetypes.includes(this.item.mimetype)
    },
    isPresentation() {
      let mimetypes = [
        'odp',
        'vnd.oasis.opendocument.presentation',
        'mspowerpoint',
        'powerpoint',
        'vnd.ms-powerpoint',
        'x-mspowerpoint',
        'vnd.openxmlformats-officedocument.presentationml.presentation',
      ]
      return mimetypes.includes(this.item.mimetype)
    },
    isZip() {
      let mimetypes = ['zip', 'rar']
      return mimetypes.includes(this.item.mimetype)
    },
    isPlain() {
      let mimetypes = ['txt', 'rtf', 'plain']
      return mimetypes.includes(this.item.mimetype)
    },
    isCsv() {
      let mimetypes = ['csv']
      return mimetypes.includes(this.item.mimetype)
    },
    isImage() {
      return this.item.type === 'image'
    },
    isVideo() {
      return this.item.type === 'video'
    },
    isAudio() {
      let mimetypes = ['mpeg', 'mp3', 'mp4', 'wan', 'flac']
      return (
        mimetypes.includes(this.item.mimetype) && this.item.type === 'audio'
      )
    },
    canShowMobileOptions() {
      return !(this.sharedDetail && this.sharedDetail.type === 'file')
    },
    timeStamp() {
      return this.item.deleted_at
        ? this.$t('item_thumbnail.deleted_at', this.item.deleted_at)
        : this.item.created_at
    },
    folderItems() {
      return this.item.deleted_at ? this.item.trashed_items : this.item.items
    },
    isDeleted() {
      return this.item.deleted_at ? true : false
    },
  },

  created() {
    this.itemName = this.item.name

    events.$on('mobileSelecting:start', () => {
      this.multiSelectMode = true
      this.$store.commit('CLEAR_FILEINFO_DETAIL')
    })

    events.$on('mobileSelecting:stop', () => {
      this.multiSelectMode = false
      this.$store.commit('CLEAR_FILEINFO_DETAIL')
    })
  },

  methods: {
    showItemActions() {
      // Load file info detail
      this.$store.commit('CLEAR_FILEINFO_DETAIL')
      this.$store.commit('GET_FILEINFO_DETAIL', this.item)

      events.$emit('mobileMenu:show')
    },
    clickedItem(e) {
      // Disabled right click
      if (e.button === 2) return

      if (!this.$isMobile()) {
        // After click deselect new folder rename input
        document.getSelection().removeAllRanges()

        if (e.ctrlKey || (e.metaKey && !e.shiftKey)) {
          // Click + Ctrl
          if (
            this.fileInfoDetail.some(
              (item) => item.unique_id === this.item.unique_id,
            )
          ) {
            this.$store.commit('REMOVE_ITEM_FILEINFO_DETAIL', this.item)
          } else {
            this.$store.commit('GET_FILEINFO_DETAIL', this.item)
          }
        } else if (e.shiftKey) {
          // Click + Shift
          let lastItem = this.data.indexOf(
            this.fileInfoDetail[this.fileInfoDetail.length - 1],
          )
          let clickedItem = this.data.indexOf(this.item)

          // If Click + Shift + Ctrl dont remove already selected items
          if (!e.ctrlKey && !e.metaKey) {
            this.$store.commit('CLEAR_FILEINFO_DETAIL')
          }

          //Shift selecting from top to bottom
          if (lastItem < clickedItem) {
            for (let i = lastItem; i <= clickedItem; i++) {
              this.$store.commit('GET_FILEINFO_DETAIL', this.data[i])
            }
            //Shift selecting from bottom to top
          } else {
            for (let i = lastItem; i >= clickedItem; i--) {
              this.$store.commit('GET_FILEINFO_DETAIL', this.data[i])
            }
          }
        } else {
          // Click
          this.$store.commit('CLEAR_FILEINFO_DETAIL')
          this.$store.commit('GET_FILEINFO_DETAIL', this.item)
          // Open preview on whole item click for media types
          if (this.isImage || this.isVideo || this.isAudio) {
            this.goToItem()
          }
        }
      }

      if (!this.mobileMultiSelect && this.$isMobile()) {
        if (this.isFolder) {
          if (this.$isThisLocation('public')) {
            this.$store.dispatch('browseShared', [
              { folder: this.item, back: false, init: false },
            ])
          } else {
            this.$store.dispatch('getFolder', [
              { folder: this.item, back: false, init: false },
            ])
          }
        } else {
          if (this.isImage || this.isVideo || this.isAudio) {
            this.$store.commit('LOAD_FILEINFO_DETAIL', this.item)
            events.$emit('fileFullPreviewMode:show', this.item)
          }
        }
      }

      if (this.multiSelectMode && this.$isMobile()) {
        if (
          this.fileInfoDetail.some(
            (item) => item.unique_id === this.item.unique_id,
          )
        ) {
          this.$store.commit('REMOVE_ITEM_FILEINFO_DETAIL', this.item)
        } else {
          this.$store.commit('GET_FILEINFO_DETAIL', this.item)
        }
      }
      // Get target classname
      let itemClass = e.target.className

      const ignoreClasses = [
        'name',
        'icon',
        'file-link',
        'file-icon-text',
        'file-download',
        'file-download-icon',
      ]
      if (
        typeof itemClass === 'string' &&
        ignoreClasses.some((cls) => itemClass.includes(cls))
      )
        return
    },
    goToItem() {
      if (this.isImage || this.isVideo || this.isAudio) {
        // Always set immediate selection first
        this.$store.commit('LOAD_FILEINFO_DETAIL', this.item)
        // If preview dataset exists, replace with canonical object
        if (
          this.$store.getters.dataPreview &&
          this.$store.getters.dataPreview.length
        ) {
          this.$store.commit('GET_FILEINFO_DETAIL_PREVIEW', this.item)
        }
        // Refresh preview list from folder in background (optional)
        if (this.item.folder_id) {
          this.$store
            .dispatch('getFolderByIdAndSetDataForPreview', {
              folder_uid: this.item.folder_id,
              item: this.item,
            })
            .then(() => {
              // Replace selection with object instance from preview dataset (if not matched earlier)
              this.$store.commit('GET_FILEINFO_DETAIL_PREVIEW', this.item)
            })
        }
        events.$emit('fileFullPreviewMode:show', this.item)
      } else if (
        this.isFile ||
        (!this.isFolder && !this.isVideo && !this.isAudio && !this.isImage)
      ) {
        this.fileStatusDownloading = true
        this.downloadProgress = 0
        this.$showDownloadInProgress(this.item.id)
        // Use real download with progress and rely on server-provided filename
        this.$downloadFile(this.item.file_url, {
          onProgress: (percent) => {
            if (typeof percent === 'number') {
              this.downloadProgress = percent
            }
          },
        })
          .catch(() => {})
          .finally(() => {
            this.fileStatusDownloading = false
            this.downloadProgress = null
            this.$hideDownloadInProgress(this.item.id)
          })
      } else if (this.isFolder) {
        //Clear selected data after open another folder
        this.$store.commit('CLEAR_FILEINFO_DETAIL')

        if (this.$isThisLocation('public')) {
          this.$store.dispatch('browseShared', [
            { folder: this.item, back: false, init: false },
          ])
        } else {
          this.$store.dispatch('getFolder', [
            { folder: this.item, back: false, init: false },
          ])
        }
      }
    },
  },
}
</script>

<style scoped lang="scss">
.check-select {
  margin-right: 10px;
  margin-left: 3px;
  position: absolute;
  top: -10px;
  z-index: 5;
  left: 0px;
}

.check-select-folder {
  margin-right: 10px;
  margin-left: 3px;
  position: absolute;
  top: 8px;
  z-index: 5;
  left: 10px;
}

.select-box {
  width: 20px;
  height: 20px;
  background-color: $light_background;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 5px;
  box-shadow: 0 3px 15px 2px hsla(220, 36%, 16%, 0.05);
}

.select-box-active {
  background-color: $theme;

  .icon {
    stroke: white;
  }
}

.show-actions {
  cursor: pointer;
  padding: 4px 26px;

  .icon-action {
    @include font-size(12);
  }

  path {
    fill: $theme;
  }
}

.file-wrapper {
  user-select: none;
  position: relative;
  text-align: center;
  display: inline-block;
  vertical-align: text-top;
  width: 100%;

  .item-name {
    display: block;
    padding-left: 8px;
    padding-right: 8px;
    line-height: 16px;

    .item-size,
    .item-length {
      padding-top: 6px;
      @include font-size(11);
      font-weight: 400;
      color: rgba($text, 0.6);
      display: inline-block;
    }

    .item-info {
      display: block;
      line-height: 1;
    }

    .name {
      color: $text;
      @include font-size(13);
      font-weight: 600;
      max-height: 40px;
      line-height: 14px;
      overflow: hidden;
      text-overflow: ellipsis;
      word-break: break-all;

      &[contenteditable] {
        -webkit-user-select: text;
        user-select: text;
      }

      &[contenteditable='true']:hover {
        text-decoration: underline;
      }

      &.actived {
        max-height: initial;
      }
    }
  }

  &.selected {
    .file-item {
      background: $light_background;
    }
  }

  .file-item {
    border: 2px dashed transparent;
    width: 150px;
    margin: 0 auto;
    cursor: pointer;
    position: relative;
    padding: 4px 0 4px 0;

    &.no-clicked {
      background: white !important;

      .item-name {
        .name {
          color: $text !important;
        }
      }
    }

    .file-item-context {
      z-index: 98;
      background: rgba(244, 245, 246, 0.8);
      display: none;
      width: 100%;
      height: 100%;
      text-align: center;
      .file-download {
        z-index: 99;
        background: #164194;
        border-radius: 99em;
        color: #ffffff;
        width: 40px;
        height: 40px;
        position: absolute;
        top: 40%;
        left: 0;
        right: 0;
        margin: 0 auto;
        font-size: 24px;
        text-align: center;
        padding-top: 7px;
      }
    }

    .file-item-status {
      z-index: 98;
      display: none;
      width: 100%;
      height: 100%;
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      border-radius: 5px;
      .file-download-status {
        z-index: 99;
        position: absolute;
        top: 30%;
        left: 0;
        margin: 0 auto;
        color: #164194;
        font-size: 14px;
        padding: 0 10px;
        font-weight: 700;
      }
      &.show-status {
        background: rgba(244, 245, 246, 0.8) !important;
        display: block;
      }
    }

    &:hover,
    &.is-clicked {
      border-radius: 8px;
      background: $light_background;

      .file-item-context {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        display: block;
      }
    }
  }

  .icon-item {
    text-align: center;
    position: relative;
    height: 110px;
    margin-bottom: 0;
    display: flex;
    align-items: center;

    .file-link {
      display: block;
    }

    .file-icon {
      @include font-size(100);
      margin: 0 auto;

      path {
        fill: #fafafc;
        stroke: #dfe0e8;
        stroke-width: 1;
      }

      &.file-icon-default {
        path {
          fill: #868e96;
          stroke: #868e96;
          stroke-width: 10;
        }
      }

      &.file-icon-pdf {
        path {
          fill: #d6221b;
          stroke: #d6221b;
          stroke-width: 10;
        }
      }

      &.file-icon-document {
        path {
          fill: #295394;
          stroke: #295394;
          stroke-width: 10;
        }
      }

      &.file-icon-spreadsheet {
        path {
          fill: #396a45;
          stroke: #396a45;
          stroke-width: 10;
        }
      }

      &.file-icon-presentation {
        path {
          fill: #c7523a;
          stroke: #c7523a;
          stroke-width: 10;
        }
      }

      &.file-icon-zip {
        path {
          fill: #f3ae30;
          stroke: #f3ae30;
          stroke-width: 10;
        }
      }

      &.file-icon-csv {
        path {
          fill: #c28c21;
          stroke: #c28c21;
          stroke-width: 10;
        }
      }

      &.file-icon-plain {
        path {
          fill: #764c18;
          stroke: #764c18;
          stroke-width: 10;
        }
      }
    }

    .file-icon-text {
      margin: 5px auto 0;
      position: absolute;
      text-align: center;
      left: 0;
      right: 0;
      color: $theme;
      @include font-size(12);
      font-weight: 600;
      user-select: none;
      max-width: 65px;
      max-height: 20px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .image {
      max-width: 95%;
      object-fit: cover;
      user-select: none;
      height: 110px;
      border-radius: 5px;
      margin: 0 auto;
      pointer-events: none;
    }

    .folder {
      width: 80px;
      height: 80px;
      margin: auto;

      :deep(.folder-icon) {
        @include font-size(80);
      }
    }
  }
}

@media only screen and (max-width: 960px) {
  .file-wrapper {
    .icon-item {
      margin-bottom: 15px;
    }
  }
}

@media only screen and (max-width: 690px) {
  .file-wrapper {
    .file-item {
      width: 120px;
    }

    .icon-item {
      margin-bottom: 10px;
      height: 90px;

      .file-icon {
        @include font-size(75);
      }

      .file-icon-text {
        @include font-size(12);
      }

      .folder {
        width: 75px;
        height: 75px;
        margin-top: 0;
        margin-bottom: 0;

        :deep(.folder-icon) {
          @include font-size(75);
        }
      }

      .image {
        width: 90px;
        height: 90px;
      }
    }

    .item-name .name {
      @include font-size(13);
      line-height: 0.9;
      max-height: 30px;
    }
  }
}
</style>
